import { useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/Auth/AuthContext';
import toastUtils from '../../utils/toastUtils';
import { i18n } from '../../translate/i18n';

const useKanbanWebSocket = ({ 
  onTaskUpdate, 
  onAutomationUpdate, 
  onCollaborationUpdate,
  enabled = true 
}) => {
  const { socket, user } = useContext(AuthContext);

  const handleTaskUpdate = useCallback((data) => {
    if (!enabled) return;

    switch (data.action) {
      case 'task-created':
        toastUtils.success(i18n.t('kanban.messages.taskCreated'), 'Task Criada');
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      case 'task-updated':
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      case 'task-moved':
        if (data.user && data.user.id !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.taskMovedByUser', { user: data.user.name }), 'Card Movido');
        }
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      case 'task-assigned':
        if (data.assignee && data.assignee.id === user.id) {
          toastUtils.success(i18n.t('kanban.messages.taskAssignedToYou', { 
            task: data.task?.title || `#${data.task?.id}` 
          }), 'Task Criada');
        } else if (data.user && data.user.id !== user.id) {
          toastUtils.info(i18n.t('kanban.messages.taskAssignedByUser', { user: data.user.name }), 'Status Alterado');
        }
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      case 'task-deleted':
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      case 'progress-updated':
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      case 'subtask-added':
      case 'subtask-completed':
        onTaskUpdate && onTaskUpdate(data);
        break;
      
      default:
        onTaskUpdate && onTaskUpdate(data);
    }
  }, [enabled, onTaskUpdate, user.id]);

  const handleAutomationUpdate = useCallback((data) => {
    if (!enabled) return;

    switch (data.action) {
      case 'automation-triggered':
        if (data.automation && data.task) {
          toastUtils.info(i18n.t('kanban.messages.automationTriggered', {
            automation: data.automation.name,
            task: data.task.title || `#${data.task.id}`
          }), 'Status Alterado');
        }
        onAutomationUpdate && onAutomationUpdate(data);
        break;
      
      case 'automation-created':
      case 'automation-updated':
      case 'automation-deleted':
        onAutomationUpdate && onAutomationUpdate(data);
        break;
      
      default:
        onAutomationUpdate && onAutomationUpdate(data);
    }
  }, [enabled, onAutomationUpdate]);

  const handleCollaborationUpdate = useCallback((data) => {
    if (!enabled) return;

    switch (data.action) {
      case 'user-viewing-task':
        if (data.user && data.user.id !== user.id) {
          // Show subtle indication that another user is viewing the same task
          onCollaborationUpdate && onCollaborationUpdate(data);
        }
        break;
      
      case 'user-editing-task':
        if (data.user && data.user.id !== user.id) {
          toastUtils.warning(i18n.t('kanban.messages.userEditingTask', {
            user: data.user.name,
            task: data.task?.title || `#${data.task?.id}`
          }), 'Status Alterado');
          onCollaborationUpdate && onCollaborationUpdate(data);
        }
        break;
      
      case 'comment-added':
        if (data.user && data.user.id !== user.id && data.mentions?.includes(user.id)) {
          toastUtils.info(i18n.t('kanban.messages.mentionedInComment', {
            user: data.user.name,
            task: data.task?.title || `#${data.task?.id}`
          }), 'Status Alterado');
        }
        onCollaborationUpdate && onCollaborationUpdate(data);
        break;
      
      default:
        onCollaborationUpdate && onCollaborationUpdate(data);
    }
  }, [enabled, onCollaborationUpdate, user.id]);

  const handleBulkOperation = useCallback((data) => {
    if (!enabled) return;

    if (data.user && data.user.id !== user.id) {
      toastUtils.info(i18n.t('kanban.messages.bulkOperationByUser', {
        user: data.user.name,
        operation: data.operation,
        count: data.affectedTasks?.length || 0
      }), 'Status Alterado');
    }
    
    onTaskUpdate && onTaskUpdate(data);
  }, [enabled, onTaskUpdate, user.id]);

  useEffect(() => {
    if (!socket || !enabled) return;

    const companyId = user.companyId;
    
    // Subscribe to Kanban-specific events
    const events = [
      { name: `company-${companyId}-kanban-task`, handler: handleTaskUpdate },
      { name: `company-${companyId}-kanban-automation`, handler: handleAutomationUpdate },
      { name: `company-${companyId}-kanban-collaboration`, handler: handleCollaborationUpdate },
      { name: `company-${companyId}-kanban-bulk`, handler: handleBulkOperation },
    ];

    // Register event listeners
    events.forEach(({ name, handler }) => {
      socket.on(name, handler);
    });

    // Cleanup function
    return () => {
      events.forEach(({ name, handler }) => {
        socket.off(name, handler);
      });
    };
  }, [
    socket, 
    enabled, 
    user.companyId,
    handleTaskUpdate,
    handleAutomationUpdate,
    handleCollaborationUpdate,
    handleBulkOperation
  ]);

  // Methods to emit events
  const emitTaskView = useCallback((taskId) => {
    if (socket && enabled) {
      socket.emit('kanban-task-view', {
        taskId,
        userId: user.id,
        companyId: user.companyId,
        timestamp: new Date().toISOString()
      });
    }
  }, [socket, enabled, user.id, user.companyId]);

  const emitTaskEdit = useCallback((taskId) => {
    if (socket && enabled) {
      socket.emit('kanban-task-edit', {
        taskId,
        userId: user.id,
        companyId: user.companyId,
        timestamp: new Date().toISOString()
      });
    }
  }, [socket, enabled, user.id, user.companyId]);

  const emitBulkOperation = useCallback((operation, taskIds) => {
    if (socket && enabled) {
      socket.emit('kanban-bulk-operation', {
        operation,
        taskIds,
        userId: user.id,
        companyId: user.companyId,
        timestamp: new Date().toISOString()
      });
    }
  }, [socket, enabled, user.id, user.companyId]);

  return {
    emitTaskView,
    emitTaskEdit,
    emitBulkOperation
  };
};

export default useKanbanWebSocket;